import { ScrollableArea } from "@nova/component-scrollable-area"
import { useCallback, useEffect, useState } from "react"
import classNames from "classnames"
import * as Icons from "@nova/component-icons"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    clearSelection,
    selectRenamingLayerId,
    selectSelectedLayerIds,
    selectSingleLayer,
    setRenamingLayerId,
} from "../slice.new"
import {
    getLocalStorageValue,
    setLocalStorageValue,
    useLocalStorageValue,
} from "tools/store/localStorageRegistry"
import { selectActiveContextMenu } from "features/popups/contextMenu"
import { setActiveContextMenu } from "features/popups/slice"
import { ContextMenu } from "features/data-management/workspace/TODO-put-in-package/ContextMenu"
import {
    SidePanelCategoryData,
    SidePanelEntityData,
    useFilteredAndSortedAssetsSidePanelData,
} from "../useSidePanelAssets"
import { SidePanelListEmptyState } from "../SidePanelListEmptyState"
import { animationClass } from "features/data-management/workspace/constants"
import { ExpandableContent } from "@nova/component-expandable-content"
import { createPortal } from "react-dom"

export function SidePanelAssetsList() {
    const dispatch = useAppDispatch()
    const assets = useFilteredAndSortedAssetsSidePanelData()
    useEffect(() => {
        if (typeof window === "undefined") return
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key !== "Escape") return
            dispatch(clearSelection())
        }
        window.addEventListener("keydown", handleEscape)
        return () => window.removeEventListener("keydown", handleEscape)
    }, [dispatch])
    return (
        <ScrollableArea
            className="flex-1 min-h-0 px-3"
            shouldPreventScrollbarShifting
            onClick={() => {
                dispatch(clearSelection())
                dispatch(setActiveContextMenu(undefined))
                dispatch(setRenamingLayerId({ id: undefined }))
            }}
        >
            {!!assets.length ? (
                <div className="flex flex-col gap-[2px] relative">
                    {assets.map((data, i) => {
                        return <AssetsCategoryWrapper key={i} data={data} />
                    })}
                </div>
            ) : (
                <SidePanelListEmptyState />
            )}
        </ScrollableArea>
    )
}

function AssetsCategoryWrapper({
    data,
}: {
    data: { categoryId: string; categoryData: SidePanelCategoryData }
}) {
    const expandedAssetCategories = useLocalStorageValue("sidePanelExpandedAssetCategories")
    const isExpanded = expandedAssetCategories.includes(data.categoryId)
    const [isHovered, setIsHovered] = useState(false)
    return (
        <>
            <div
                className={classNames("w-full rounded-lg select-none", {
                    "bg-gray-700": isHovered,
                })}
            >
                <div
                    className="flex items-center w-full py-2 pr-2 cursor-pointer group"
                    onClick={() => {
                        const current = getLocalStorageValue("sidePanelExpandedAssetCategories")
                        setLocalStorageValue(
                            "sidePanelExpandedAssetCategories",
                            isExpanded
                                ? current.filter((category) => category !== data.categoryId)
                                : [...current, data.categoryId]
                        )
                    }}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                >
                    <div className="pl-1">
                        <Icons.Icon
                            Icon={Icons.ArrowDropdownRight}
                            className={classNames(animationClass, "text-gray-400 cursor-pointer", {
                                "rotate-90": isExpanded,
                            })}
                        />
                    </div>
                    <data.categoryData.SidePanelCategoryHeader
                        entityCount={data.categoryData.entitiesData.length}
                    />
                </div>
                <ExpandableContent isOpen={isExpanded}>
                    <div className="flex flex-col gap-[2px] p-1">
                        {data.categoryData.entitiesData.map((data) => {
                            return (
                                <ListItemWrapper
                                    key={data.entityId}
                                    id={data.entityId}
                                    plugin={data.plugin}
                                />
                            )
                        })}
                    </div>
                </ExpandableContent>
            </div>
        </>
    )
}

function ListItemWrapper({ id, plugin }: { id: string; plugin: SidePanelEntityData["plugin"] }) {
    const dispatch = useAppDispatch()
    const [isHovered, setIsHovered] = useState(false)
    const selectedLayerIds = useAppSelector(selectSelectedLayerIds)
    const isSelected = selectedLayerIds.includes(id)
    const activeSidePanelContextMenu = useAppSelector(selectActiveContextMenu("SidePanelItem"))
    const contextMenuIsForThisItem = activeSidePanelContextMenu?.data.layerId === id
    const renamingLayerId = useAppSelector(selectRenamingLayerId)
    const isRenaming = renamingLayerId === id

    const onClick = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault()
            e.stopPropagation()
            if (activeSidePanelContextMenu) {
                dispatch(setActiveContextMenu(undefined))
                return
            }
            if (renamingLayerId && renamingLayerId !== id) {
                dispatch(setRenamingLayerId({ id: undefined }))
            }
            dispatch(selectSingleLayer({ id }))
        },
        [activeSidePanelContextMenu, dispatch, id, renamingLayerId]
    )
    const onContextMenu = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault()
            if (e.metaKey || e.ctrlKey) onClick(e)
            if (e.button !== 2) return
            dispatch(
                setActiveContextMenu({
                    type: "SidePanelItem",
                    data: {
                        layerId: id,
                        selectedLayerIds: isSelected ? selectedLayerIds : [id],
                    },
                    position: { left: e.clientX, top: e.clientY },
                })
            )
        },
        [dispatch, id, isSelected, onClick, selectedLayerIds]
    )

    const contextMenuSections = plugin.useContextMenuSections(id)

    return (
        <div
            className={classNames("w-full rounded-lg cursor-pointer select-none overflow-hidden", {
                "bg-blue-700": isSelected && !isRenaming,
                "bg-white-100": isHovered && !isSelected && !isRenaming,
                "outline outline-blue-500": isRenaming,
            })}
            onClick={onClick}
            onContextMenu={onContextMenu}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <plugin.SidePanelListItem id={id} />
            {contextMenuIsForThisItem &&
                createPortal(
                    <ContextMenu
                        sections={contextMenuSections}
                        onBlur={() => dispatch(setActiveContextMenu(undefined))}
                        isFixedPosition={true}
                        top={activeSidePanelContextMenu.position.top}
                        left={activeSidePanelContextMenu.position.left}
                    />,
                    document.getElementById("portal")!
                )}
        </div>
    )
}
